import { moveToFulfilled } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { AllCampaignsLoadFulfilledAction } from '../../domains/allCampaigns/loadActions.types';
import type { CampaignsListLoadMoreFulfilledAction } from '../../domains/allCampaigns/loadMoreAllCampaignsActions.types';
import type {
  CampaignEditFulfilledAction,
  CampaignPublishFulfilledAction,
} from '../../domains/campaignEdit/actions.types';
import type { CampaignFollowFulfilledAction } from '../../domains/campaignFollowing/followActions.types';
import type { CampaignUnfollowFulfilledAction } from '../../domains/campaignFollowing/unfollowActions.types';
import type { CampaignLoadFulfilledAction } from '../../domains/campaigns/actions.types';
import type { MyLeaderSponsoredCampaignsLoadFulfilledAction } from '../../domains/myLeaderSponsoredCampaigns/actions.types';
import type { TrendingCampaignsLoadFulfilledAction } from '../../domains/trendingCampaigns/loadActions.types';
import type { UserSidebarLoadFulfilledAction } from '../../domains/userSidebarLoad/actions.types';

import type { CampaignFollowing, CampaignFollowingEntities } from './types';

export type CampaignFollowingAction =
  | AllCampaignsLoadFulfilledAction
  | CampaignsListLoadMoreFulfilledAction
  | UserSidebarLoadFulfilledAction
  | CampaignLoadFulfilledAction
  | MyLeaderSponsoredCampaignsLoadFulfilledAction
  | CampaignFollowFulfilledAction
  | CampaignUnfollowFulfilledAction
  | CampaignEditFulfilledAction
  | CampaignPublishFulfilledAction
  | TrendingCampaignsLoadFulfilledAction;

const initialState: CampaignFollowingEntities = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const moveMyLeaderSponsoredCampaignsToFulfilled = (
  state: CampaignFollowingEntities,
  action: MyLeaderSponsoredCampaignsLoadFulfilledAction
) => {
  const entities = action.payload.entities.campaignFollowing;

  if (!entities) {
    return state;
  }

  if (action.payload.shouldUpdateFollowers) {
    return moveToFulfilled(state, entities);
  }

  const entitiesWithOldFollowers: Record<string, CampaignFollowing> = Object.keys(entities).reduce((acc, key) => {
    const { users, ...rest } = entities[key];
    if (state.fulfilled.byId[key] !== undefined) {
      return {
        ...acc,
        [key]: Object.assign({}, state.fulfilled.byId[key], rest),
      };
    }

    return {
      ...acc,
      [key]: entities[key],
    };
  }, {});

  return moveToFulfilled(state, entitiesWithOldFollowers);
};

const reducer: Reducer<CampaignFollowingEntities, CampaignFollowingAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'ALL_CAMPAIGNS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignFollowing);
    case 'ALL_CAMPAIGNS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignFollowing);
    case 'USER_SIDEBAR.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignFollowing);
    case 'CAMPAIGN.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignFollowing);
    case 'CAMPAIGN.EDIT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignFollowing);
    case 'CAMPAIGN.PUBLISH_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignFollowing);
    case 'MY_LEADER_SPONSORED_CAMPAIGNS.LOAD_FULFILLED':
      return moveMyLeaderSponsoredCampaignsToFulfilled(state, action);
    case 'CAMPAIGN.FOLLOW_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignFollowing);
    case 'CAMPAIGN.UNFOLLOW_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignFollowing);
    case 'TRENDING_CAMPAIGNS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.campaignFollowing);
    default:
      return state;
  }
};

export default reducer;
